import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ChatWidget from '@/components/ChatWidget';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Bell, Mail, Globe, ShieldCheck, Zap, Check,
  ArrowRight, Smartphone, MapPin, BellRing, Lock,
  ChevronDown, ChevronUp, AlertTriangle, XCircle,
  RefreshCw, FileText, ListFilter, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DHMAD_FALLBACK_URL = 'https://dhmad.tn/';

const LandingPage = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [dhmadLink, setDhmadLink] = useState(DHMAD_FALLBACK_URL);

  // Aktueller DHMAD-Zahlungslink für tunesische Besucher (siehe Dashboard.js)
  useEffect(() => {
    const fetchDhmadLink = async () => {
      try {
        const response = await axios.get(`${API}/payment/dhmad-link`);
        if (response.data?.url) setDhmadLink(response.data.url);
      } catch (error) {
        // Fällt auf DHMAD_FALLBACK_URL zurück
      }
    };
    fetchDhmadLink();
  }, []);

  // Ersetzt {platzhalter} in einem übersetzten Text durch fett dargestellte
  // React-Knoten, damit Hervorhebungen auch in übersetzten (en/fr) Strings
  // erhalten bleiben.
  const interpolateBold = (template, replacements) => {
    const keys = Object.keys(replacements).join('|');
    const parts = template.split(new RegExp(`\\{(${keys})\\}`, 'g'));
    return parts.map((part, i) =>
      replacements[part] !== undefined
        ? <strong className="text-[#FF3B30]" key={i}>{replacements[part]}</strong>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const countries = {
    from: ['UK', 'Ireland', 'Tunisia', 'Egypt', 'Algeria', 'Morocco', 'Nigeria', 'Thailand', 'Iraq', 'Cameroon', 'Tanzania', 'UAE', 'Qatar', 'China', 'India'],
    to: ['France', 'Germany', 'Spain', 'Italy', 'Belgium', 'Portugal', 'Malta', 'Netherlands', 'Hungary', 'Iceland', 'Ireland', 'Switzerland']
  };

  const features = [
    { icon: Globe, title: t('feature_1_title'), desc: t('feature_1_desc') },
    { icon: Bell, title: t('feature_2_title'), desc: t('feature_2_desc') },
    { icon: Zap, title: t('feature_3_title'), desc: t('feature_3_desc') },
    { icon: ListFilter, title: 'Filter by Visa Type', desc: 'Get alerts matched to your exact visa type — Tourism, Business, Work, Student and 14 more categories.' },
  ];

  const pricingFeatures = [
    t('pricing_feature_1'),
    t('pricing_feature_2'),
    t('pricing_feature_3'),
    t('pricing_feature_4'),
    t('pricing_feature_5'),
  ];

  const faqs = [
    { q: t('faq_1_q'), a: t('faq_1_a') },
    { q: t('faq_2_q'), a: t('faq_2_a') },
    { q: t('faq_3_q'), a: t('faq_3_a') },
    { q: t('faq_4_q'), a: t('faq_4_a') },
    { q: t('faq_5_q'), a: t('faq_5_a') },
    { q: 'Can I get alerts for a specific visa type only?', a: 'Yes! When you subscribe, you can select your Visa Type (Tourism, Business, Work, Student, Family Visit and more) in your Dashboard preferences. We\'ll only alert you about appointments matching that visa type, so you\'re never notified about slots that don\'t apply to you.' },
  ];

  const securityFeatures = [
    { icon: XCircle, title: t('security_no_phishing'), desc: t('security_no_phishing_desc') },
    { icon: Lock, title: t('security_no_links'), desc: t('security_no_links_desc') },
    { icon: AlertTriangle, title: t('security_not_tls'), desc: t('security_not_tls_desc') },
    { icon: Check, title: t('security_cancel'), desc: t('security_cancel_desc') },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />

      {/* Hero Banner */}
      <div className="bg-[#FF3B30] text-white py-2 mt-16" data-testid="hero-banner">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm font-medium flex items-center justify-center gap-2">
            <Bell className="w-4 h-4 animate-pulse" />
            {t('hero_banner')}
            <Button
              size="sm"
              variant="secondary"
              className="ml-2 h-7 bg-white text-[#FF3B30] hover:bg-gray-100 font-semibold text-xs"
              onClick={handleGetStarted}
            >
              {t('pricing_cta')}
            </Button>
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#FF3B30]/10 text-[#FF3B30] px-3 py-1 rounded-sm text-sm font-medium mb-6">
                <Bell className="w-4 h-4" />
                <span>Real-time Visa Alerts</span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A0A0A] leading-tight mb-6" data-testid="hero-title">
                {t('hero_title')}
              </h1>

              <p className="text-lg text-gray-600 mb-8 max-w-lg" data-testid="hero-subtitle">
                {t('hero_subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  size="lg"
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-semibold px-8 h-12 rounded-sm"
                  onClick={handleGetStarted}
                  data-testid="hero-cta"
                >
                  {t('hero_cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-semibold px-8 h-12 rounded-sm"
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-pricing-btn"
                >
                  {t('nav_pricing')}
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              {/* Alert Notification Visual */}
              <div className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] rounded-sm p-8 shadow-2xl" data-testid="hero-visual">
                <div className="space-y-4">
                  {/* Notification 1 */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-sm p-4 shadow-lg flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-[#FF3B30] rounded-full flex items-center justify-center animate-pulse-ring">
                      <BellRing className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-heading font-bold text-[#0A0A0A]">France Visa Available!</p>
                      <p className="text-sm text-gray-600">Tunis TLS Center • Tourism • 2 slots open</p>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-sm font-medium">NOW</span>
                  </motion.div>

                  {/* Notification 2 */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white/90 rounded-sm p-4 shadow-md flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-[#0A0A0A] rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-heading font-semibold text-[#0A0A0A]">Email Alert Sent</p>
                      <p className="text-sm text-gray-500">Germany visa appointment • Business</p>
                    </div>
                    <span className="text-xs text-gray-400">2m ago</span>
                  </motion.div>

                  {/* Notification 3 */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 }}
                    className="bg-white/80 rounded-sm p-4 shadow-sm flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-heading font-semibold text-[#0A0A0A]">SMS Delivered</p>
                      <p className="text-sm text-gray-500">+216 ** *** **89</p>
                    </div>
                    <span className="text-xs text-gray-400">5m ago</span>
                  </motion.div>
                </div>

                <p className="mt-4 text-center text-xs text-gray-500">Example notifications</p>

                {/* Stats bar */}
                <div className="mt-6 pt-6 border-t border-gray-700 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">24/7</p>
                    <p className="text-xs text-gray-400">Monitoring</p>
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-[#FF3B30]">&lt;1min</p>
                    <p className="text-xs text-gray-400">Alert Speed</p>
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">99%</p>
                    <p className="text-xs text-gray-400">Uptime</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
              {t('features_title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('features_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-sm hover:shadow-md transition-shadow rounded-sm" data-testid={`feature-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#0A0A0A] rounded-sm flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-[#0A0A0A] mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {feature.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* How it works visual */}
          <div className="mt-16 grid md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-[#F9FAFB] rounded-sm p-8" data-testid="notification-demo">
                {/* Phone mockup with notifications */}
                <div className="max-w-[280px] mx-auto bg-[#0A0A0A] rounded-3xl p-3 shadow-2xl">
                  <div className="bg-white rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-heading font-bold text-sm">VisaAlerts</span>
                      <span className="text-xs text-gray-400">Now</span>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="font-semibold text-sm text-green-800">New Slot!</p>
                      <p className="text-xs text-green-600">France Visa - Tourism - March 15</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="font-semibold text-sm text-blue-800">Email Sent</p>
                      <p className="text-xs text-blue-600">Check your inbox</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="font-semibold text-sm text-purple-800">SMS Delivered</p>
                      <p className="text-xs text-purple-600">+216 ** *** **89</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2 space-y-6">
              <div className="flex items-start gap-4 stagger-item">
                <div className="w-8 h-8 bg-[#FF3B30] rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">1</div>
                <div>
                  <h4 className="font-heading font-semibold text-[#0A0A0A]">Create Your Account</h4>
                  <p className="text-gray-600 text-sm">Sign up in seconds and set your preferences</p>
                </div>
              </div>
              <div className="flex items-start gap-4 stagger-item">
                <div className="w-8 h-8 bg-[#FF3B30] rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">2</div>
                <div>
                  <h4 className="font-heading font-semibold text-[#0A0A0A]">Configure Alerts</h4>
                  <p className="text-gray-600 text-sm">Choose your country, visa type and notification preferences</p>
                </div>
              </div>
              <div className="flex items-start gap-4 stagger-item">
                <div className="w-8 h-8 bg-[#FF3B30] rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">3</div>
                <div>
                  <h4 className="font-heading font-semibold text-[#0A0A0A]">Get Notified Instantly</h4>
                  <p className="text-gray-600 text-sm">Receive alerts the moment new slots open for your visa type</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="py-16 md:py-24" data-testid="countries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
              {t('countries_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Destination Countries */}
            <div>
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#FF3B30]" />
                {t('countries_to')}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {countries.to.map((country) => {
                  const countryData = {
                    'France': {
                      flag: '🇫🇷',
                      landmark: 'Eiffel Tower',
                      image: 'https://images.unsplash.com/photo-1594507401263-b2d02b358a1c?w=400&q=80'
                    },
                    'Germany': {
                      flag: '🇩🇪',
                      landmark: 'Brandenburg Gate',
                      image: 'https://images.unsplash.com/photo-1560969184-10fe8719e047?w=400&q=80'
                    },
                    'Spain': {
                      flag: '🇪🇸',
                      landmark: 'Sagrada Familia',
                      image: 'https://images.unsplash.com/photo-1728249987965-3943d53dd634?w=400&q=80'
                    },
                    'Italy': {
                      flag: '🇮🇹',
                      landmark: 'Colosseum',
                      image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&q=80'
                    },
                    'Belgium': {
                      flag: '🇧🇪',
                      landmark: 'Atomium',
                      image: 'https://images.unsplash.com/photo-1719133541350-2fcd8d0b49d4?w=400&q=80'
                    },
                    'Portugal': {
                      flag: '🇵🇹',
                      landmark: 'Belém Tower',
                      image: 'https://images.unsplash.com/photo-1608095266750-26604ba60ad5?w=400&q=80'
                    },
                    'Malta': {
                      flag: '🇲🇹',
                      landmark: 'Valletta',
                      image: 'https://images.unsplash.com/photo-1560365337-6f42f70dd874?w=400&q=80'
                    },
                    'Netherlands': {
                      flag: '🇳🇱',
                      landmark: 'Amsterdam',
                      image: 'https://images.unsplash.com/photo-1605309692204-12e7e481989e?w=400&q=80'
                    },
                    'Hungary': {
                      flag: '🇭🇺',
                      landmark: 'Budapest Parliament',
                      image: 'https://images.unsplash.com/photo-1674916513051-7058e45c926f?w=400&q=80'
                    },
                    'Iceland': {
                      flag: '🇮🇸',
                      landmark: 'Northern Lights',
                      image: 'https://images.unsplash.com/photo-1666003415555-1634ff3cd022?w=400&q=80'
                    },
                    'Ireland': {
                      flag: '🇮🇪',
                      landmark: 'Temple Bar Dublin',
                      image: 'https://images.unsplash.com/photo-1664263563064-e1da6d055782?w=400&q=80'
                    },
                    'Switzerland': {
                      flag: '🇨🇭',
                      landmark: 'Swiss Alps',
                      image: 'https://images.unsplash.com/photo-1719784909327-b81448b046d0?w=400&q=80'
                    }
                  };
                  const data = countryData[country] || { flag: '🌍', landmark: '', image: '' };
                  return (
                    <Card
                      key={country}
                      className="rounded-sm border-0 shadow-lg overflow-hidden relative h-32 group cursor-pointer"
                      data-testid={`country-to-${country.toLowerCase()}`}
                    >
                      {/* Background Image */}
                      <div
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                        style={{ backgroundImage: `url(${data.image})` }}
                      ></div>
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                      {/* Content */}
                      <div className="relative h-full flex flex-col justify-end p-4 text-white">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl">{data.flag}</span>
                          <p className="font-heading font-bold text-lg drop-shadow-lg">{country}</p>
                        </div>
                        <p className="text-xs text-white/80">{data.landmark}</p>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* From Countries */}
            <div>
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#0A0A0A]" />
                {t('countries_from')}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {countries.from.map((country) => {
                  const flags = {
                    'Tunisia': '🇹🇳',
                    'Morocco': '🇲🇦',
                    'Nigeria': '🇳🇬',
                    'Thailand': '🇹🇭',
                    'Iraq': '🇮🇶',
                    'Cameroon': '🇨🇲',
                    'Tanzania': '🇹🇿',
                    'Egypt': '🇪🇬',
                    'UK': '🇬🇧',
                    'Ireland': '🇮🇪',
                    'Algeria': '🇩🇿',
                    'UAE': '🇦🇪',
                    'Qatar': '🇶🇦',
                    'China': '🇨🇳',
                    'India': '🇮🇳'
                  };
                  return (
                    <Card
                      key={country}
                      className="p-3 rounded-sm border hover:shadow-md hover:border-[#FF3B30] transition-all flex items-center gap-3"
                      data-testid={`country-from-${country.toLowerCase()}`}
                    >
                      <span className="text-xl">{flags[country]}</span>
                      <span className="font-medium text-[#0A0A0A] text-sm">{country}</span>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Visa Type Filtering callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 bg-[#0A0A0A] rounded-sm p-6 md:p-8"
          >
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-14 h-14 bg-[#FF3B30]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-[#FF3B30]" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-heading font-bold text-white text-lg mb-1">
                  Alerts Personalized by Visa Type
                </h3>
                <p className="text-gray-400 text-sm">
                  Tourism, Business, Work, Student, Family Visit, Medical Treatment and 12 more categories — choose your visa type in your Dashboard and we'll only alert you about matching appointments.
                </p>
              </div>
              <Button
                className="bg-[#FF3B30] hover:bg-[#E5352B] text-white font-semibold rounded-sm whitespace-nowrap"
                onClick={handleGetStarted}
              >
                Set My Visa Type
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 bg-gradient-to-br from-[#0A0A0A] via-[#1a1a1a] to-[#0A0A0A]" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              {t('pricing_title')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t('pricing_subtitle')}
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white rounded-sm overflow-hidden relative border-2 border-[#FF3B30]" data-testid="pricing-card">
                {/* PROMO badge */}
                <div className="bg-gradient-to-r from-[#FF3B30] to-[#FF6B5B] text-white text-center py-3">
                  <div className="flex items-center justify-center gap-2">
                    <span className="animate-pulse">🔥</span>
                    <span className="font-bold text-sm">{t('pricing_promo_badge')}</span>
                    <span className="animate-pulse">🔥</span>
                  </div>
                  <p className="text-xs text-white/90 mt-1">{t('pricing_promo_text')}</p>
                </div>

                <CardContent className="p-8">
                  {/* Header */}
                  <div className="text-center pb-6 border-b border-gray-100">
                    <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BellRing className="w-8 h-8 text-[#FF3B30]" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-[#0A0A0A] mb-1">Premium Alerts</h3>
                    <p className="text-gray-500 text-sm">Full access to all features</p>
                  </div>

                  {/* Price with strikethrough old price */}
                  <div className="text-center py-6">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <span className="text-2xl text-gray-400 line-through">{t('pricing_currency')}{t('pricing_old_price')}</span>
                      <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">-20%</span>
                    </div>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-lg text-[#FF3B30]">{t('pricing_currency')}</span>
                      <span className="text-6xl font-heading font-bold text-[#FF3B30]">{t('pricing_price')}</span>
                      <span className="text-gray-500 ml-1">{t('pricing_period')}</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">Billed monthly • Cancel anytime</p>

                    {/* Tunesien-Angebot: eigene kleine "Karte in der Karte" mit
                        farbigem Header-Ribbon (Marke-Rot-Verlauf) + sauberer
                        Preiszeile darunter, statt der alten Pill-Lösung —
                        wirkt wie ein eigenständiges, professionelles Angebot
                        statt einer nachträglich drangeklebten Zeile. */}
                    <div className="mt-5 mx-auto max-w-xs overflow-hidden rounded-2xl border border-[#FF3B30]/15 bg-white shadow-sm shadow-[#FF3B30]/5">
                      <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-[#FF3B30] to-[#FF6B5B] px-4 py-2">
                        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-white">
                          <span className="text-sm leading-none">🇹🇳</span>
                          {t('pricing_tnd_exclusive')}
                        </span>
                        <span className="rounded-full bg-white/25 px-2 py-0.5 text-[10px] font-extrabold text-white">
                          {t('pricing_promo_badge_tnd')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-heading font-bold text-[#0A0A0A]">
                            {t('pricing_price_tnd')} <span className="text-sm font-semibold text-gray-500">TND</span>
                          </span>
                          <span className="text-sm text-gray-400 line-through">{t('pricing_old_price_tnd')} TND</span>
                        </div>
                        <div className="flex items-center gap-1 text-[11px] text-gray-400">
                          <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                          DHMAD.tn
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {pricingFeatures.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3 p-2 rounded-sm hover:bg-gray-50 transition-colors">
                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-[#0A0A0A] font-medium">{feature}</span>
                      </li>
                    ))}
                    <li className="flex items-center gap-3 p-2 rounded-sm hover:bg-gray-50 transition-colors">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[#0A0A0A] font-medium">Alerts filtered by Visa Type (Tourism, Business, Work, Study & more)</span>
                    </li>
                  </ul>

                  {/* BIGGER Money-Back Guarantee Badge */}
                  <div className="mb-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl shadow-sm">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                        <RefreshCw className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-green-800 text-base">{t('risk_free_title')}</p>
                        <p className="text-green-700 text-sm mt-1">{t('risk_free_desc')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Zwei Zahlungsoptionen direkt in der Karte: Stripe (Karte) und
                      DHMAD.tn (Escrow, TND) — ersetzt den alten "ein Button + separater
                      Marketing-Block darunter"-Aufbau. Für nicht eingeloggte Besucher
                      führt der Stripe-Button weiterhin zur Registrierung (handleGetStarted);
                      der DHMAD-Link funktioniert direkt, auch ohne Konto. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      className="bg-[#FF3B30] hover:bg-[#E5352B] text-white font-bold h-14 rounded-sm text-base shadow-lg shadow-[#FF3B30]/25 transition-all hover:shadow-xl hover:shadow-[#FF3B30]/30"
                      onClick={handleGetStarted}
                      data-testid="pricing-cta"
                    >
                      {t('pricing_buy_stripe')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>

                    <Button
                      asChild
                      variant="outline"
                      className="border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 font-bold h-14 rounded-sm text-base"
                      data-testid="landing-dhmad-link"
                    >
                      <a href={dhmadLink} target="_blank" rel="noopener noreferrer">
                        🇹🇳 {t('pricing_buy_dhmad')} ({t('pricing_price_tnd')} TND)
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                  </div>

                  {/* Trust badges */}
                  <div className="mt-6 pt-6 border-t border-gray-100 flex items-center justify-center gap-4 text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <ShieldCheck className="w-4 h-4" />
                      <span>Secure Payment</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Zap className="w-4 h-4" />
                      <span>Instant Access</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Vertrauenshinweis zu DHMAD — "DHMAD.tn" innerhalb des Satzes in
                  Rot (via interpolateBold oben), kein separater Link/Button
                  mehr darunter, da der Haupt-CTA-Button in der Karte bereits
                  zu DHMAD führt. */}
              <p className="mt-4 text-center text-sm text-gray-600">
                🇹🇳 {interpolateBold(t('pricing_dhmad_desc'), { dhmad: 'DHMAD.tn' })}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Email Preview Section - What your alert looks like */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Mail className="w-4 h-4" />
              {t('email_preview_badge')}
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
              {t('email_preview_title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('email_preview_subtitle')}
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200"
            >
              {/* Email Header */}
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-sm text-gray-500 ml-2">customer_service@visago-alerts.online</span>
                </div>
              </div>

              {/* Email Subject */}
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF3B30] flex items-center justify-center">
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0A0A0A]">Visa-Alerts: France Appointments Available!</p>
                    <p className="text-sm text-gray-500">To: you@example.com</p>
                  </div>
                </div>
              </div>

              {/* Email Content Preview */}
              <div className="p-6">
                <div className="bg-gradient-to-r from-[#FF3B30] to-[#FF6B5B] text-white rounded-lg p-6 mb-4 text-center">
                  <h3 className="font-bold text-xl mb-2">VISA-ALERTS</h3>
                  <p className="text-white/80 text-sm">Appointment Notification Service</p>
                </div>

                <div className="bg-green-100 text-green-800 rounded-full px-4 py-2 text-sm font-medium text-center mb-4 inline-block w-full">
                  New Appointments Available - France - Tourism
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-[#FF3B30] mb-4">
                  <p className="text-gray-700">
                    New visa appointments at <strong className="text-[#FF3B30]">TLS Tunisia</strong> for <strong className="text-green-600">France</strong> visa (<strong>Tourism</strong>) are now available!
                  </p>
                  <p className="text-gray-600 text-sm mt-2">
                    Book your appointment quickly before they are gone.
                  </p>
                </div>

                <div className="bg-amber-50 rounded-lg p-3 text-center text-amber-800 text-sm">
                  We are NOT affiliated with TLS Contact. Visit the official website directly to book.
                </div>
              </div>

              {/* Email Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
                <p className="text-center text-gray-500 text-xs">
                  VisaAlerts - Your trusted visa appointment notification service
                </p>
              </div>
            </motion.div>

            <div className="text-center mt-8">
              <Button
                className="bg-[#FF3B30] hover:bg-[#E5352B] text-white font-bold h-12 px-8 rounded-sm shadow-lg"
                onClick={handleGetStarted}
              >
                {t('email_preview_cta')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <ShieldCheck className="w-4 h-4" />
              Secure & Trusted
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
              {t('security_title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('security_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityFeatures.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full border-0 shadow-sm hover:shadow-md transition-shadow rounded-sm bg-[#F9FAFB]">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#0A0A0A] rounded-full flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-[#0A0A0A] mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {feature.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Important Notice Box */}
          <div className="mt-12 bg-yellow-50 border border-yellow-200 rounded-sm p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-heading font-semibold text-[#0A0A0A] mb-2">Important Notice / Avis Important</h4>
                <p className="text-gray-700 text-sm mb-3">
                  <strong>EN:</strong> VisaAlerts is an <strong>independent notification service</strong>. We are NOT affiliated with TLS Contact, VFS Global, or BLS International
                  and do NOT book appointments on your behalf. We simply monitor for available slots and notify you
                  so YOU can book directly on the official websites (visas-fr.tlscontact.com, vfsglobal.com, blsinternational.com).
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>FR:</strong> VisaAlerts est un <strong>service de notification indépendant</strong>. Nous ne sommes PAS affiliés à TLS Contact, VFS Global ou BLS International
                  et nous ne réservons PAS de rendez-vous pour vous. Nous surveillons simplement les créneaux disponibles et vous notifions
                  afin que VOUS puissiez réserver directement sur les sites officiels (visas-fr.tlscontact.com, vfsglobal.com, blsinternational.com).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
              {t('faq_title')}
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <Card
                  className="rounded-sm border cursor-pointer overflow-hidden"
                  onClick={() => toggleFaq(idx)}
                >
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors">
                      <h3 className="font-heading font-semibold text-[#0A0A0A] pr-4">
                        {faq.q}
                      </h3>
                      {openFaq === idx ? (
                        <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </div>
                    <AnimatePresence>
                      {openFaq === idx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
                            <p className="text-gray-600">{faq.a}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
            Ready to Secure Your Visa Appointment?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            Never miss a visa appointment slot announcement. Start getting alerts today.
          </p>
          <Button
            size="lg"
            className="bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white font-semibold px-8 h-12 rounded-sm"
            onClick={handleGetStarted}
            data-testid="cta-button"
          >
            {t('hero_cta')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
      <ChatWidget page="landing" />
    </div>
  );
};

export default LandingPage;
